import '../assets/NcColorPicker-Clz7MjCA.css';
import { Chrome } from "@ckpack/vue-color";
import { defineComponent, resolveComponent, createBlock, openBlock, withCtx, createElementVNode, normalizeClass, createVNode, createElementBlock, createCommentVNode, Transition, Fragment, renderList, normalizeStyle, createTextVNode, toDisplayString, renderSlot, normalizeProps, guardReactiveProps } from "vue";
import { d as defaultPalette } from "./colors-AMTxjrbV.mjs";
import { c as createElementId } from "./createElementId-DhjFt1I9.mjs";
import { r as register, i as t1, a as t } from "./_l10n-dinIMiE_.mjs";
import { A as ArrowLeft } from "./ArrowLeft-CanHG70H.mjs";
import { C as Check } from "./Check-5i4xKnkl.mjs";
import { D as DotsHorizontal } from "./DotsHorizontal-AMVAlTNH.mjs";
import { N as NcButton } from "./NcButton-Byg8-ta1.mjs";
import { N as NcPopover } from "./NcPopover-CzrFCS-2.mjs";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
register(t1);
const HEX_REGEX = /^#([a-f0-9]{3}|[a-f0-9]{6})$/i;
const _sfc_main = defineComponent({
  name: "NcColorPicker",
  components: {
    ArrowLeft,
    Check,
    Chrome,
    DotsHorizontal,
    NcButton,
    NcPopover
  },
  props: {
    /**
     * A HEX color that represents the initial value of the picker
     */
    modelValue: {
      type: String,
      required: true
    },
    /**
     * Set to `true` to enable advanced fields including HEX, RGB, and HSL
     */
    advancedFields: {
      type: Boolean,
      default: false
    },
    /**
     * Limit selectable colors to only the provided palette
     */
    paletteOnly: {
      type: Boolean,
      default: false
    },
    /**
     * Provide a custom array of colors to show.
     * Can be either an array of string hexadecimal colors,
     * or an array of object with a `color` property with hexadecimal color string,
     * and a `name` property for accessibility.
     *
     * @type {string[] | {color: string, name: string}[]}
     */
    palette: {
      type: Array,
      default: () => [...defaultPalette],
      validator: (palette) => palette.every(
        (item) => typeof item === "string" && HEX_REGEX.test(item) || typeof item === "object" && item.color && HEX_REGEX.test(item.color)
      )
    },
    /**
     * Selector for the popover container
     */
    container: {
      type: [String, Object, Element, Boolean],
      default: "body"
    }
  },
  emits: [
    "submit",
    "close",
    "update:open",
    "update:modelValue"
  ],
  data() {
    return {
      currentColor: this.modelValue,
      advanced: false,
      ariaBack: t("Back"),
      ariaMore: t("More options")
    };
  },
  computed: {
    normalizedPalette() {
      return this.palette.map((item) => ({
        color: typeof item === "object" ? item.color : item,
        name: typeof item === "object" && item.name ? item.name : t("A color with a HEX value {hex}", { hex: item.color })
      }));
    },
    uid() {
      return createElementId();
    },
    contrastColor() {
      const black = "#000000";
      const white = "#FFFFFF";
      return this.calculateLuma(this.currentColor) > 0.5 ? black : white;
    }
  },
  watch: {
    modelValue(color) {
      this.currentColor = color;
    }
  },
  methods: {
    t,
    /**
     * Submit a picked colour and close picker
     * @param {Function} hideCallback callback to close popover
     */
    handleConfirm(hideCallback) {
      this.$emit("submit", this.currentColor);
      hideCallback();
      this.advanced = false;
    },
    handleClose() {
      this.$emit("close");
      this.$emit("update:open", false);
    },
    /**
     * Inner navigations
     */
    handleBack() {
      this.advanced = false;
    },
    handleMoreSettings() {
      this.advanced = true;
    },
    /**
     * Pick a colour
     *
     * @param {string} color the picked color
     */
    pickColor(color) {
      if (typeof color !== "string") {
        color = this.currentColor.hex;
      }
      this.currentColor = color;
      this.$emit("update:modelValue", color);
    },
    /**
     * Calculate luminance of provided hex color
     *
     * @param {string} color the hex color
     */
    calculateLuma(color) {
      const [red, green, blue] = this.hexToRGB(color);
      return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
    },
    /**
     * Convert hex color to RGB
     *
     * @param {string} hex the hex color
     */
    hexToRGB(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
    }
  }
});
const _hoisted_1 = ["aria-label"];
const _hoisted_2 = {
  key: 0,
  class: "color-picker__simple"
};
const _hoisted_3 = ["aria-label", "name", "checked", "onClick"];
const _hoisted_4 = {
  key: 0,
  class: "color-picker__navigation"
};
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Check = resolveComponent("Check");
  const _component_Chrome = resolveComponent("Chrome");
  const _component_ArrowLeft = resolveComponent("ArrowLeft");
  const _component_NcButton = resolveComponent("NcButton");
  const _component_DotsHorizontal = resolveComponent("DotsHorizontal");
  const _component_NcPopover = resolveComponent("NcPopover");
  return openBlock(), createBlock(_component_NcPopover, {
    "popup-role": "dialog",
    container: _ctx.container,
    onApplyHide: _ctx.handleClose
  }, {
    trigger: withCtx((slotProps) => [
      renderSlot(_ctx.$slots, "default", normalizeProps(guardReactiveProps(slotProps)), void 0, true)
    ]),
    default: withCtx((slotProps) => [
      createElementVNode("div", {
        role: "dialog",
        class: normalizeClass(["color-picker", { "color-picker--advanced-fields": _ctx.advanced && _ctx.advancedFields }]),
        "aria-modal": "true",
        "aria-label": _ctx.t("Color picker")
      }, [
        createVNode(Transition, {
          name: "slide",
          mode: "out-in"
        }, {
          default: withCtx(() => [
            !_ctx.advanced ? (openBlock(), createElementBlock("div", _hoisted_2, [
              (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.normalizedPalette, ({ color, name }, index) => {
                return openBlock(), createElementBlock("label", {
                  key: index,
                  style: normalizeStyle({ backgroundColor: color }),
                  class: normalizeClass(["color-picker__simple-color-circle", { "color-picker__simple-color-circle--active": color === _ctx.currentColor }])
                }, [
                  color === _ctx.currentColor ? (openBlock(), createBlock(_component_Check, {
                    key: 0,
                    size: 20,
                    "fill-color": _ctx.contrastColor
                  }, null, 8, ["fill-color"])) : createCommentVNode("", true),
                  createElementVNode("input", {
                    type: "radio",
                    class: "hidden-visually",
                    "aria-label": name,
                    name: `color-picker-${_ctx.uid}`,
                    checked: color === _ctx.currentColor,
                    onClick: ($event) => _ctx.pickColor(color)
                  }, null, 8, _hoisted_3)
                ], 6);
              }), 128))
            ])) : (openBlock(), createBlock(_component_Chrome, {
              key: 1,
              modelValue: _ctx.currentColor,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event) => _ctx.currentColor = $event),
                _ctx.pickColor
              ],
              class: "color-picker__advanced",
              "disable-alpha": true,
              "disable-fields": !_ctx.advancedFields
            }, null, 8, ["modelValue", "disable-fields", "onUpdate:modelValue"]))
          ]),
          _: 1
        }),
        !_ctx.paletteOnly ? (openBlock(), createElementBlock("div", _hoisted_4, [
          _ctx.advanced ? (openBlock(), createBlock(_component_NcButton, {
            key: 0,
            "aria-label": _ctx.ariaBack,
            variant: "tertiary",
            onClick: _ctx.handleBack
          }, {
            icon: withCtx(() => [
              createVNode(_component_ArrowLeft, { size: 20 })
            ]),
            _: 1
          }, 8, ["aria-label", "onClick"])) : (openBlock(), createBlock(_component_NcButton, {
            key: 1,
            "aria-label": _ctx.ariaMore,
            variant: "tertiary",
            onClick: _ctx.handleMoreSettings
          }, {
            icon: withCtx(() => [
              createVNode(_component_DotsHorizontal, { size: 20 })
            ]),
            _: 1
          }, 8, ["aria-label", "onClick"])),
          createVNode(_component_NcButton, {
            variant: "primary",
            onClick: ($event) => _ctx.handleConfirm(slotProps.hide)
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(_ctx.t("Choose")), 1)
            ]),
            _: 2
          }, 1032, ["onClick"])
        ])) : createCommentVNode("", true)
      ], 10, _hoisted_1)
    ]),
    _: 3
  }, 8, ["container", "onApplyHide"]);
}
const NcColorPicker = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__scopeId", "data-v-180efb02"]]);
export {
  NcColorPicker as N
};
//# sourceMappingURL=NcColorPicker-DGwM1Zki.mjs.map
